export function getSlottedElementName(slot: HTMLSlotElement): string | null {
  const childNodes = slot.assignedNodes({ flatten: true });
  if (childNodes.length > 0) {
    const element = childNodes[0] as HTMLElement;
    return element.tagName?.toLowerCase() || null;
  }
  return null;
}

/**
 * Creates a slot change handler that updates component properties based on slot names
 * @param component - The component instance containing the properties to update
 * @param slotPropertyMap - Map of slot names to component property names
 * @returns A slot change event handler function
 */
export function createSlotChangeHandler<T extends Record<string, any>>(
  component: T,
  slotPropertyMap: Record<string, keyof T>
) {
  return (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const elementName = getSlottedElementName(slot);

    const property = slotPropertyMap[slot.name];
    if (property) {
      component[property] = elementName as T[keyof T];
    }
  };
}

// SSR-safe initial slot property assignment
// Iterates over known slot names and sets the mapped property to the slotted element's tag name (or null)
export function setInitialSlotProperties<T extends Record<string, any>>(
  component: T,
  slotPropertyMap: Record<string, keyof T>
) {
  for (const slotName in slotPropertyMap) {
    const slotElement = component.querySelector(`[slot="${slotName}"]`) as HTMLSlotElement | null;
    if (slotElement) {
      const elementName = slotElement.nodeName.toLowerCase();
      const property = slotPropertyMap[slotName];
      component[property] = elementName as T[keyof T];
    }
  }
}

// Use this function to sync attributes from a slotted element to the host component.
// e.g. when a slotted button has disabled set on it and we want to reflect that on the host.
export function syncSlottedAttributes(
  component: Record<string, any>,
  observer: globalThis.MutationObserver | null,
  observedElement: HTMLElement | null,
  observedSlotAttributes: (string | object)[]
): globalThis.MutationObserver | null {
  if (!observedElement) {
    observer?.disconnect();
    return null;
  }

  // Observe future changes to the observed elements attributes
  if (!observer) {
    observer = new globalThis.MutationObserver((mutations) => {
      for (const m of mutations) {
        observedSlotAttributes.forEach((prop) => {
          if (typeof prop === "object") {
            const [observedAttr, componentProp] = Object.entries(prop)[0];
            if (m.type === "attributes" && m.attributeName === observedAttr) {
              component[componentProp] = observedElement.hasAttribute(observedAttr);
            }
          } else if (m.type === "attributes" && m.attributeName === prop) {
            component[prop] = observedElement.hasAttribute(prop);
          }
        });
      }
    });
  }

  observer?.observe(observedElement, {
    attributes: true,
    attributeFilter: observedSlotAttributes.map((prop) =>
      typeof prop === "object" ? Object.keys(prop)[0] : prop
    ),
  });

  return observer;
}
